import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Download, CalendarCheck } from 'lucide-react';
import { Button } from './ui/button';
import BookingModal from './BookingModal';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [showBooking, setShowBooking] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleDownloadProfile = async () => {
    setIsDownloading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/download/company-profile`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Yusen_Design_Company_Profile.pptx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setDownloadError(true);
      setTimeout(() => setDownloadError(false), 3000);
    } finally {
      setIsDownloading(false);
    }
  };

  const navItems = [
    { label: 'Home', id: 'home' },
    { label: 'Portfolio', id: 'portfolio' },
    { label: 'Services', id: 'services' },
    { label: 'Materials', id: 'building-materials' },
    { label: 'About', id: 'about' },
    { label: 'Certifications', id: 'certifications' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'Contact', id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0">
            <button
              onClick={() => scrollToSection('home')}
              className="flex items-center group transition-all duration-300"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_yusenspaces/artifacts/6ud4osh2_logolarge.png"
                alt="Yusen Design & Contract Pte Ltd"
                className="h-16 w-auto transition-all duration-300 group-hover:scale-110 group-hover:drop-shadow-lg"
              />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-sm font-medium transition-colors duration-300 hover:text-blue-600 ${
                  isScrolled ? 'text-gray-700' : 'text-white'
                }`}
              >
                {item.label}
              </button>
            ))}
            <Button
              data-testid="download-profile-btn-desktop"
              onClick={handleDownloadProfile}
              disabled={isDownloading}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50 transition-all duration-300 transform hover:scale-105"
            >
              <Download className="w-4 h-4 mr-2" />
              {isDownloading ? 'Downloading...' : 'Company Profile'}
            </Button>
            <Button
              data-testid="book-consultation-btn-desktop"
              onClick={() => setShowBooking(true)}
              className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300 transform hover:scale-105"
            >
              <CalendarCheck className="w-4 h-4 mr-2" />
              Book Consultation
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white transition-all duration-300 transform hover:scale-105"
            >
              <Phone className="w-4 h-4 mr-2" />
              Get Quote
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <div className="px-4 py-6 space-y-4">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 transition-colors"
              >
                {item.label}
              </button>
            ))}
            <Button
              data-testid="download-profile-btn-mobile"
              onClick={handleDownloadProfile}
              disabled={isDownloading}
              variant="outline"
              className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
            >
              <Download className="w-4 h-4 mr-2" />
              {isDownloading ? 'Downloading...' : 'Download Company Profile'}
            </Button>
            <Button
              data-testid="book-consultation-btn-mobile"
              onClick={() => { setShowBooking(true); setIsMobileMenuOpen(false); }}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              <CalendarCheck className="w-4 h-4 mr-2" />
              Book Free Consultation
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Phone className="w-4 h-4 mr-2" />
              Get Quote
            </Button>
          </div>
        </div>
      )}
      <BookingModal isOpen={showBooking} onClose={() => setShowBooking(false)} />
    </header>
  );
};

export default Header;